/**
 * teleport.js — node-based teleport locomotion and comfort effects.
 *
 * GDD 12.3 is non-negotiable: no artificial locomotion in VR. Movement between
 * stalls is point-and-teleport onto fixed NODES, executed as an instant blink
 * with a brief vignette. Desktop mode is exempt and may walk freely (WASD),
 * because the comfort problem does not exist without a headset.
 *
 * Snapping to nodes rather than arbitrary ground keeps the player at a known
 * play position in front of each stall, which the stall framework relies on.
 *
 * @module core/teleport
 */

import * as THREE from 'three';
import { COMFORT, EVENTS, PALETTE } from '../constants.js';
import { bus } from './events.js';
import { input } from './input.js';
import { xr } from './xr.js';
import { haptics } from './haptics.js';

/**
 * @typedef {Object} TeleportNode
 * @property {string} id
 * @property {THREE.Vector3} position Floor position the player stands at.
 * @property {number} yaw Facing direction applied on arrival, radians.
 * @property {string} [label]
 * @property {string} [group] e.g. 'stall', 'hub', 'landmark'
 */

class TeleportSystem {
  constructor() {
    /** @type {Map<string, TeleportNode>} */
    this.nodes = new Map();
    /** @type {THREE.Scene} */ this.scene = null;
    /** @type {THREE.Object3D} */ this.rig = null;
    /** @type {THREE.Camera} */ this.camera = null;

    this.enabled = true;
    this.aiming = false;
    /** @type {TeleportNode|null} */ this.candidate = null;

    this._markers = new Map();
    this._arc = null;
    this._arcPoints = [];
    this._blink = null;
    this._vignette = null;
    this._blinkState = null;
    this._vignetteTarget = 0;
    this._vignetteValue = 0;
    /**
     * When an avatar owns the camera rig it must perform the move itself,
     * otherwise its follow-camera code overwrites the rig on the next frame
     * and the teleport silently snaps back.
     * @type {((position: THREE.Vector3, yaw: number|null) => void)|null}
     */
    this._mover = null;
  }

  /**
   * Delegate the actual move to whoever owns the camera rig.
   * @param {(position: THREE.Vector3, yaw: number|null) => void} fn
   */
  setMover(fn) {
    this._mover = fn;
  }

  /**
   * @param {THREE.Scene} scene
   * @param {THREE.Object3D} rig The camera rig moved on teleport.
   * @param {THREE.Camera} camera
   */
  init(scene, rig, camera) {
    this.scene = scene;
    this.rig = rig;
    this.camera = camera;

    this._buildArc();
    this._buildComfortOverlays();
    return this;
  }

  // ── Node registry ─────────────────────────────────────────────────────────

  /**
   * Register a teleport destination.
   * @param {{id: string, position: THREE.Vector3|{x,y,z}, yaw?: number,
   *          label?: string, group?: string, marker?: boolean}} def
   * @returns {TeleportNode}
   */
  addNode(def) {
    const node = {
      id: def.id,
      position: def.position instanceof THREE.Vector3
        ? def.position.clone()
        : new THREE.Vector3(def.position.x, def.position.y || 0, def.position.z),
      yaw: def.yaw ?? 0,
      label: def.label || def.id,
      group: def.group || 'hub',
    };
    this.nodes.set(node.id, node);
    if (def.marker !== false && this.scene) this._addMarker(node);
    return node;
  }

  /** @param {string} id */
  removeNode(id) {
    this.nodes.delete(id);
    const m = this._markers.get(id);
    if (m) {
      m.parent?.remove(m);
      this._markers.delete(id);
    }
  }

  /** @returns {string[]} Every registered node id. */
  listNodes() {
    return [...this.nodes.keys()];
  }

  /** @param {string} id @returns {TeleportNode|undefined} */
  getNode(id) {
    return this.nodes.get(id);
  }

  /**
   * @param {THREE.Vector3} position
   * @param {number} [maxDistance=Infinity]
   * @returns {TeleportNode|null}
   */
  nearestNode(position, maxDistance = Infinity) {
    let best = null;
    let bestD = maxDistance;
    for (const n of this.nodes.values()) {
      const d = n.position.distanceTo(position);
      if (d < bestD) { bestD = d; best = n; }
    }
    return best;
  }

  // ── Movement ──────────────────────────────────────────────────────────────

  /**
   * Teleport to a registered node.
   * @param {string} id
   * @returns {boolean} False if the node does not exist.
   */
  to(id) {
    const node = this.nodes.get(id);
    if (!node) {
      console.warn(`[teleport] unknown node "${id}"`);
      return false;
    }
    this.toPosition(node.position, node.yaw, node.id);
    return true;
  }

  /**
   * Teleport to an arbitrary spot with a blink.
   * @param {THREE.Vector3|{x,y,z}} position
   * @param {number} [yaw]
   * @param {string} [nodeId]
   */
  toPosition(position, yaw = null, nodeId = null) {
    const target = position instanceof THREE.Vector3
      ? position.clone()
      : new THREE.Vector3(position.x, position.y || 0, position.z);

    this._startBlink(() => {
      if (this._mover) {
        this._mover(target, yaw);
        return;
      }
      if (!this.rig) return;
      this.rig.position.set(target.x, target.y, target.z);
      if (yaw !== null && yaw !== undefined) this.rig.rotation.y = yaw + Math.PI;
    });

    this.pulseVignette();
    haptics.play('right', 'teleport');
    bus.emit(EVENTS.TELEPORT, { nodeId, position: target.clone(), yaw });
  }

  /**
   * Run a callback at the darkest point of a blink. Used for mode transitions
   * (GDD 3.3) where the camera jumps but the player is not teleporting.
   * @param {() => void} atBlack
   * @param {() => void} [onComplete]
   */
  blink(atBlack, onComplete) {
    this._startBlink(atBlack, onComplete);
    this.pulseVignette();
  }

  /** Show the comfort vignette briefly (used for any camera translation). */
  pulseVignette() {
    this._vignetteTarget = COMFORT.vignetteStrength;
    setTimeout(() => { this._vignetteTarget = 0; }, 180);
  }

  // ── Frame update ──────────────────────────────────────────────────────────

  update(dt) {
    this._updateBlink(dt);
    this._updateVignette(dt);
    if (!this.enabled) {
      this._hideArc();
      return;
    }
    if (input.isVR) this._updateVRAiming();
    else this._updateDesktopAiming();
  }

  /**
   * VR: push the thumbstick forward to arm the arc, release to commit.
   * @private
   */
  _updateVRAiming() {
    const hand = input.getHand('right');
    const pushed = hand && -hand.thumbstick.y > COMFORT.teleportDeadzone;

    if (pushed) {
      this.aiming = true;
      const controller = xr.getController('right');
      if (controller) this._aimFrom(controller);
    } else if (this.aiming) {
      this.aiming = false;
      this._hideArc();
      if (this.candidate) {
        this.to(this.candidate.id);
        this.candidate = null;
      }
    }
  }

  /**
   * Desktop: T teleports to the nearest node ahead of the player. Desktop can
   * also just walk, so this is a convenience rather than the primary path.
   * @private
   */
  _updateDesktopAiming() {
    if (!input.wasPressed('teleport')) return;
    const from = new THREE.Vector3();
    this.rig.getWorldPosition(from);
    const forward = new THREE.Vector3(0, 0, -1).applyQuaternion(this.camera.getWorldQuaternion(new THREE.Quaternion()));
    forward.y = 0;
    forward.normalize();

    let best = null;
    let bestScore = -Infinity;
    for (const n of this.nodes.values()) {
      const to = n.position.clone().sub(from);
      const dist = to.length();
      if (dist < 0.5 || dist > COMFORT.teleportMaxRange) continue;
      const score = to.normalize().dot(forward) * 2 - dist * 0.06;
      if (score > bestScore) { bestScore = score; best = n; }
    }
    if (best) this.to(best.id);
  }

  /**
   * Trace a parabola from a controller and snap to the nearest node.
   * @param {THREE.Object3D} controller
   * @private
   */
  _aimFrom(controller) {
    const origin = controller.getWorldPosition(new THREE.Vector3());
    const dir = new THREE.Vector3(0, 0, -1)
      .applyQuaternion(controller.getWorldQuaternion(new THREE.Quaternion()));

    const speed = 8;
    const g = -9.81;
    const pts = this._arcPoints;
    let landed = null;
    for (let i = 0; i < pts.length; i++) {
      const t = i * 0.045;
      const p = pts[i];
      p.set(
        origin.x + dir.x * speed * t,
        origin.y + dir.y * speed * t + 0.5 * g * t * t,
        origin.z + dir.z * speed * t,
      );
      if (p.y <= 0 && !landed) landed = p.clone();
    }
    this._arc.geometry.setFromPoints(pts);
    this._arc.visible = true;

    const prev = this.candidate;
    this.candidate = landed ? this.nearestNode(landed, 3.5) : null;
    if (this.candidate !== prev) {
      this._highlightMarkers();
      if (this.candidate) haptics.play('right', 'ui');
    }
  }

  /** @private */
  _hideArc() {
    if (this._arc) this._arc.visible = false;
    if (this.candidate) {
      this.candidate = null;
      this._highlightMarkers();
    }
  }

  // ── Comfort overlays ──────────────────────────────────────────────────────

  /**
   * Blink: fade to black, run the move at the darkest point, fade back in.
   * @param {() => void} atBlack
   * @param {() => void} [onComplete]
   * @private
   */
  _startBlink(atBlack, onComplete) {
    this._blinkState = {
      phase: 'out', t: 0, atBlack, onComplete, done: false,
    };
    // Without a blink mesh the move must still happen.
    if (!this._blink) {
      atBlack();
      this._blinkState = null;
      onComplete?.();
    }
  }

  /** @private */
  _updateBlink(dt) {
    const s = this._blinkState;
    if (!s || !this._blink) return;
    s.t += dt;
    const mat = this._blink.material;

    if (s.phase === 'out') {
      mat.opacity = Math.min(1, s.t / COMFORT.blinkOutTime);
      if (s.t >= COMFORT.blinkOutTime) {
        s.phase = 'hold';
        s.t = 0;
        mat.opacity = 1;
        if (!s.done) { s.atBlack(); s.done = true; }
      }
    } else if (s.phase === 'hold') {
      if (s.t >= COMFORT.blinkHoldTime) { s.phase = 'in'; s.t = 0; }
    } else {
      mat.opacity = Math.max(0, 1 - s.t / COMFORT.blinkInTime);
      if (s.t >= COMFORT.blinkInTime) {
        mat.opacity = 0;
        this._blinkState = null;
        s.onComplete?.();
      }
    }
    this._blink.visible = mat.opacity > 0.001;
  }

  /** @private */
  _updateVignette(dt) {
    const rate = 1 / Math.max(0.01, COMFORT.vignetteFadeTime);
    const diff = this._vignetteTarget - this._vignetteValue;
    this._vignetteValue += THREE.MathUtils.clamp(diff, -rate * dt, rate * dt);
    if (this._vignette) {
      this._vignette.material.opacity = this._vignetteValue;
      this._vignette.visible = this._vignetteValue > 0.005;
    }
  }

  /** @private */
  _buildComfortOverlays() {
    // Inside-out sphere around the head — works in VR where a DOM overlay cannot.
    const blinkGeo = new THREE.SphereGeometry(0.28, 12, 8);
    const blinkMat = new THREE.MeshBasicMaterial({
      color: 0x000000, side: THREE.BackSide, transparent: true, opacity: 0, depthTest: false,
    });
    this._blink = new THREE.Mesh(blinkGeo, blinkMat);
    this._blink.renderOrder = 10_000;
    this._blink.frustumCulled = false;
    this._blink.visible = false;
    this.camera.add(this._blink);

    const vigGeo = new THREE.PlaneGeometry(1.6, 1.2);
    const vigMat = new THREE.MeshBasicMaterial({
      map: makeVignetteTexture(), transparent: true, opacity: 0, depthTest: false,
    });
    this._vignette = new THREE.Mesh(vigGeo, vigMat);
    this._vignette.position.set(0, 0, -0.6);
    this._vignette.renderOrder = 9_999;
    this._vignette.frustumCulled = false;
    this._vignette.visible = false;
    this.camera.add(this._vignette);
  }

  /** @private */
  _buildArc() {
    this._arcPoints = Array.from({ length: 40 }, () => new THREE.Vector3());
    const geo = new THREE.BufferGeometry().setFromPoints(this._arcPoints);
    const mat = new THREE.LineBasicMaterial({ color: PALETTE.ticket, transparent: true, opacity: 0.8 });
    this._arc = new THREE.Line(geo, mat);
    this._arc.frustumCulled = false;
    this._arc.visible = false;
    this.scene.add(this._arc);
  }

  /** @private */
  _addMarker(node) {
    const geo = new THREE.RingGeometry(0.32, 0.44, 24);
    const mat = new THREE.MeshBasicMaterial({
      color: PALETTE.ticket, transparent: true, opacity: 0.28, side: THREE.DoubleSide,
    });
    const ring = new THREE.Mesh(geo, mat);
    ring.rotation.x = -Math.PI / 2;
    ring.position.copy(node.position);
    ring.position.y += 0.02;
    ring.name = `teleport-marker-${node.id}`;
    this.scene.add(ring);
    this._markers.set(node.id, ring);
  }

  /** @private */
  _highlightMarkers() {
    for (const [id, ring] of this._markers) {
      const on = this.candidate && this.candidate.id === id;
      ring.material.opacity = on ? 0.85 : 0.28;
      ring.scale.setScalar(on ? 1.15 : 1);
    }
  }

  /** Show or hide every node marker (hidden during stall play). */
  setMarkersVisible(visible) {
    for (const ring of this._markers.values()) ring.visible = visible;
  }
}

/** Radial gradient: clear in the middle, dark at the edges. */
function makeVignetteTexture() {
  const size = 256;
  const canvas = document.createElement('canvas');
  canvas.width = canvas.height = size;
  const ctx = canvas.getContext('2d');
  const grad = ctx.createRadialGradient(size / 2, size / 2, size * 0.24, size / 2, size / 2, size * 0.52);
  grad.addColorStop(0, 'rgba(0,0,0,0)');
  grad.addColorStop(0.7, 'rgba(0,0,0,0.55)');
  grad.addColorStop(1, 'rgba(0,0,0,1)');
  ctx.fillStyle = grad;
  ctx.fillRect(0, 0, size, size);
  const tex = new THREE.CanvasTexture(canvas);
  tex.colorSpace = THREE.SRGBColorSpace;
  return tex;
}

/** The one teleport system. */
export const teleport = new TeleportSystem();
